<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <h1>
          Run #{{ runId }}
          <span v-if="currentRun" class="grey--text body-1 ml-2">
            ({{ currentRun.subject_count }} subjects)
          </span>
        </h1>
        <v-spacer />
        <v-btn text color="primary" to="/verification">
          Back to runs
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" />
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col
        v-for="subject in currentSubjectsPage.results"
        :key="subject.id"
        cols="12" md="6"
      >
        <v-card outlined class="mb-3">
          <v-card-title class="caption grey--text">
            Subject #{{ subject.id }} · {{ subject.stratum_key }}
            <v-spacer />
            <v-chip v-if="subject.is_submitted" small color="success">submitted</v-chip>
          </v-card-title>

          <v-img
            v-if="subject.image_url"
            :src="subject.image_url"
            max-height="320"
            contain
          />

          <v-card-text>
            <div class="caption mb-2 grey--text">Click a tag to cycle judgment</div>
            <TagJudgmentChip
              v-for="tag in subject.pinned_tags"
              :key="`${subject.id}-${tag.id}`"
              :tag="tag"
              :judgment="judgmentFor(subject.id, tag.id)"
              @change="onTagJudgment(subject.id, $event)"
            />

            <v-autocomplete
              :label="`Add a tag (mark as added by verifier)`"
              :items="tagOptions(subject.id)"
              item-text="full_name"
              item-value="id"
              outlined dense clearable
              class="mt-3"
              @change="(tagId) => addTagJudgment(subject.id, tagId)"
              :value="null"
            />

            <v-textarea
              :value="notesFor(subject.id)"
              @input="(v) => setNotes(subject.id, v)"
              label="Notes"
              auto-grow rows="2" outlined dense class="mt-2"
            />

            <v-checkbox
              :input-value="flagFor(subject.id)"
              @change="(v) => setFlag(subject.id, v)"
              label="Flag this photo for re-review"
              dense hide-details
            />

            <v-btn
              color="primary" depressed
              class="mt-3"
              :loading="submitting[subject.id]"
              :disabled="subject.is_submitted"
              @click="submit(subject)"
            >
              Submit judgment
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="currentSubjectsPage.num_pages > 1">
      <v-col cols="12">
        <v-pagination
          v-model="page"
          :length="currentSubjectsPage.num_pages"
          @input="loadPage"
        />
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>Request batch reflag</v-card-title>
          <v-card-text>
            <p class="caption grey--text">
              If your spot-check shows systemic mis-tagging, request a batch
              reflag covering a project + period. SYS_ADMIN approves; on
              approval, every matching photo flips back to needs_review.
            </p>
            <v-row>
              <v-col cols="6" md="3">
                <v-text-field
                  label="Project ID"
                  v-model.number="batch.scope_project_id"
                  type="number" outlined dense
                />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field
                  label="Period start"
                  v-model="batch.scope_period_start"
                  type="date" outlined dense
                />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field
                  label="Period end (exclusive)"
                  v-model="batch.scope_period_end"
                  type="date" outlined dense
                />
              </v-col>
            </v-row>
            <v-textarea
              label="Reason"
              v-model="batch.reason"
              auto-grow rows="2" outlined dense
            />
            <v-btn
              color="warning" depressed
              :disabled="!batch.scope_project_id || !batch.scope_period_start || !batch.scope_period_end || !batch.reason"
              @click="requestBatch"
            >
              Submit reflag request
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import TagJudgmentChip from '@/components/verification/TagJudgmentChip.vue'

export default {
  name: 'VerificationRun',
  components: { TagJudgmentChip },
  data () {
    return {
      page: 1,
      submitting: {},
      // Per-subject scratch state — keyed by subject.id.
      judgments: {},   // {subjectId: {tagId: judgment}}
      addedTags: {},   // {subjectId: [tagId, ...]}
      notes: {},       // {subjectId: string}
      flags: {},       // {subjectId: bool}
      batch: {
        scope_project_id: null,
        scope_period_start: '',
        scope_period_end: '',
        reason: '',
      },
    }
  },
  computed: {
    ...mapState({
      currentRun: (s) => s.verification.currentRun,
      currentSubjectsPage: (s) => s.verification.currentSubjectsPage,
      loading: (s) => s.verification.loading,
      allTags: (s) => s.tags.tags,
    }),
    runId () { return Number(this.$route.params.id) },
  },
  mounted () {
    this.fetchSampleRun(this.runId)
    this.loadPage(1)
  },
  methods: {
    ...mapActions(['fetchSampleRun', 'fetchSubjects', 'submitSubject', 'createRereviewBatch']),
    loadPage (p) {
      this.page = p
      this.fetchSubjects({ runId: this.runId, page: p })
    },
    judgmentFor (subjectId, tagId) {
      return (this.judgments[subjectId] && this.judgments[subjectId][tagId]) || 'correct'
    },
    notesFor (subjectId) { return this.notes[subjectId] || '' },
    flagFor (subjectId) { return !!this.flags[subjectId] },
    setNotes (subjectId, value) { this.$set(this.notes, subjectId, value) },
    setFlag (subjectId, value) { this.$set(this.flags, subjectId, !!value) },
    onTagJudgment (subjectId, { tag_id, judgment }) {
      if (!this.judgments[subjectId]) this.$set(this.judgments, subjectId, {})
      this.$set(this.judgments[subjectId], tag_id, judgment)
    },
    tagOptions (subjectId) {
      // Exclude tags that are already pinned on the subject.
      const subject = this.currentSubjectsPage.results.find(s => s.id === subjectId)
      const pinnedIds = new Set(subject ? subject.pinned_tags.map(t => t.id) : [])
      const addedIds = new Set(this.addedTags[subjectId] || [])
      return this.allTags
        .filter(t => !pinnedIds.has(t.id) && !addedIds.has(t.id))
        .map(t => ({ ...t, full_name: t.full_name || t.name }))
    },
    addTagJudgment (subjectId, tagId) {
      if (!tagId) return
      const list = this.addedTags[subjectId] ? [...this.addedTags[subjectId]] : []
      if (!list.includes(tagId)) list.push(tagId)
      this.$set(this.addedTags, subjectId, list)
      this.onTagJudgment(subjectId, { tag_id: tagId, judgment: 'added_by_verifier' })
    },
    async submit (subject) {
      this.$set(this.submitting, subject.id, true)
      const judgmentMap = this.judgments[subject.id] || {}
      const judgmentList = Object.entries(judgmentMap).map(([tag_id, judgment]) => ({
        tag_id: Number(tag_id), judgment,
      }))
      // Make sure pinned tags without explicit judgments default to 'correct'
      for (const tag of subject.pinned_tags) {
        if (!(tag.id in judgmentMap)) {
          judgmentList.push({ tag_id: tag.id, judgment: 'correct' })
        }
      }
      try {
        await this.submitSubject({
          subjectId: subject.id,
          judgments: judgmentList,
          notes: this.notes[subject.id] || '',
          flagged_for_rereview: !!this.flags[subject.id],
        })
        // Reload the page to reflect is_submitted state.
        this.loadPage(this.page)
      } catch (e) {
        alert('Submit failed: ' + (e.response && e.response.data ? JSON.stringify(e.response.data) : e.message))
      } finally {
        this.$set(this.submitting, subject.id, false)
      }
    },
    async requestBatch () {
      try {
        await this.createRereviewBatch({
          scope_project_id: this.batch.scope_project_id,
          scope_period_start: new Date(this.batch.scope_period_start).toISOString(),
          scope_period_end: new Date(this.batch.scope_period_end).toISOString(),
          reason: this.batch.reason,
        })
        alert('Reflag request submitted — pending admin approval.')
        this.batch = { scope_project_id: null, scope_period_start: '', scope_period_end: '', reason: '' }
      } catch (e) {
        alert('Failed: ' + (e.response && e.response.data ? JSON.stringify(e.response.data) : e.message))
      }
    },
  },
}
</script>
